import { endpoints } from "../../../configs/Settings";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useAuth } from "../../../utils/useAuth";
import { useAxios } from "../../../utils/useAxios";

/**
 * Interface para os parâmetros de busca de estatísticas de agendamento
 */
interface IAppointmentStatsParams {
  /** Ator para filtrar os agendamentos (obrigatório) */
  actor: string;
  /** Profissional para filtrar os agendamentos (opcional) */
  professionalActor?: string;
  /** Data inicial para filtro (opcional) - formato YYYY-MM-DD */
  startDate?: string;
  /** Data final para filtro (opcional) - formato YYYY-MM-DD */
  endDate?: string;
  /** Propósito de uso da consulta (opcional) */
  purposeOfUse?: string;

  /** Status dos agendamentos (opcional) */
  status?: string[];

  /** Indica se deve retornar apenas o resumo (opcional) */
  summary?: boolean;

  /** Quantidade de registros a retornar (opcional) */
  count?: number;

  /** Filtrar por supporting-info ausente (opcional) */
  supportingInfoMissing?: boolean;

  /** Quando verdadeiro, usa _include=Appointment:actor:HealthcareService (opcional) */
  includeHealthcareService?: boolean;

  /** Quando verdadeiro, usa _include=Appointment:encounter (opcional) */
  includeEncounter?: boolean;
}

/**
 * Busca estatísticas de agendamentos com base nos parâmetros fornecidos
 * @param params Parâmetros para busca de estatísticas
 * @returns Dados dos agendamentos encontrados
 */
export async function GetAppointmentStats(params: IAppointmentStatsParams) {
  // Construir URL base
  const baseUrl = `${endpoints.STATISTICS}/Appointment`;

  // Construir parâmetros de query
  const queryParams: string[] = [];

  // Adiciona o ator (obrigatório)
  queryParams.push(`actor=${encodeURIComponent(params.actor)}`);

  // Adiciona o profissional se fornecido
  if (params.professionalActor) {
    queryParams.push(`actor=${encodeURIComponent(params.professionalActor)}`);
  }

  // Adiciona filtros de data se fornecidos
  if (params.startDate) {
    queryParams.push(`date=ge${encodeURIComponent(params.startDate)}`);
  }
  if (params.endDate) {
    queryParams.push(`date=le${encodeURIComponent(params.endDate)}`);
  }

  // Adiciona status se fornecido
  if (params.status?.length) {
    queryParams.push(`status=${encodeURIComponent(params.status.join(","))}`);
  }

  // Adiciona filtro de supporting-info se fornecido
  if (params.supportingInfoMissing) {
    queryParams.push("supporting-info:missing=false");
  }

  // Adiciona ordenação por última atualização (decrescente)
  queryParams.push(`_sort=-_lastUpdated`);

  // Adiciona summary ou include dependendo do caso
  if (params.summary) {
    queryParams.push(`_summary=count`);
  } else {
    if (params.includeHealthcareService) {
      queryParams.push(`_include=Appointment:actor:HealthcareService`);
    } else {
      queryParams.push(`_include=Appointment:patient`);
    }
    if (params.count) {
      queryParams.push(`_count=${params.count}`);
    }
  }
  if (params.includeEncounter) {
    queryParams.push(`_include=Appointment:encounter`);
  }

  // Construir URL final
  const url =
    queryParams.length > 0 ? `${baseUrl}?${queryParams.join("&")}` : baseUrl;

  // Configuração dos headers
  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    ...((await useAuth.getSubjectId()) && {
      "subject-id": await useAuth.getSubjectId(),
    }),
    ...((await useAuth.getOrganizationId()) && {
      "organization-id": await useAuth.getOrganizationId(),
    }),
    ...(params.purposeOfUse && { purposeofuse: params.purposeOfUse }),
  };

  try {
    const response = await useAxios.get(url, { headers, timeout: 20000 });

    if (response.status !== HTTPResponseStatus.Success) {
      throw new Error(`Get Appointment Stats: ${response.status}`);
    }

    return response.data;
  } catch (error: any) {
    console.error("GetAppointmentStats error:", error);
    throw new Error(
      `Erro ao buscar estatísticas: ${error?.response?.status || error.message}`
    );
  }
}
